import { useEffect, useMemo, useRef, useState } from 'react'
import { MapPin, Loader2, Plus, Check, ChevronDown } from 'lucide-react'
import { cachedGet } from '../../lib/api.js'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 transition'

/* ที่ตั้ง picker — searchable, and you can add a location that doesn't exist yet.
 *
 * There is no location table: the list is DERIVED from the ที่ตั้ง values already
 * on asset rows. So "adding" a location just means typing it — it joins the list
 * the moment the asset saves, and disappears again when the last asset using it
 * moves away. Nothing to maintain, no empty locations, no registry to keep in
 * sync with reality.
 *
 * The trade-off: renaming a location has to be done asset by asset.
 */
export default function LocationPicker({ value, onChange, branchId }) {
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const boxRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    function onDoc(e) { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  // One shared fetch per session (cachedGet dedupes across every editor open).
  // per_page is capped at 200 by the Xano endpoint, so on a very large register
  // this is a suggestion list, not an exhaustive one — you can still type anything.
  useEffect(() => {
    if (!open || rows.length) return
    setLoading(true)
    cachedGet('/api/assets?per_page=200')
      .then((r) => setRows(Array.isArray(r) ? r : (r?.items || [])))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [open, rows.length])

  // distinct locations + how many assets sit in each, most-used first
  const known = useMemo(() => {
    const m = new Map()
    for (const a of rows) {
      const loc = String(a?.location || '').trim()
      if (!loc) continue
      const key = loc.toLowerCase()
      const cur = m.get(key)
      if (cur) cur.count += 1
      else m.set(key, { name: loc, count: 1, branches: new Set() })
      if (a?.branch_id) m.get(key).branches.add(Number(a.branch_id))
    }
    return [...m.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'th'))
  }, [rows])

  const typed = String(value || '').trim()
  const matches = useMemo(() => {
    if (!typed) return known
    const q = typed.toLowerCase()
    return known.filter((k) => k.name.toLowerCase().includes(q))
  }, [known, typed])

  // Locations in this asset's own branch first — the ones actually plausible.
  const ordered = useMemo(() => {
    if (!branchId) return matches
    const b = Number(branchId)
    return [...matches].sort((x, y) => (y.branches.has(b) ? 1 : 0) - (x.branches.has(b) ? 1 : 0))
  }, [matches, branchId])

  const isNew = typed && !known.some((k) => k.name.toLowerCase() === typed.toLowerCase())

  function pick(name) {
    onChange(name)
    setOpen(false)
    inputRef.current?.blur()
  }

  return (
    <div className="relative" ref={boxRef}>
      <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <input
        ref={inputRef}
        value={value || ''}
        onChange={(e) => { onChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setOpen(false); return }
          if (e.key === 'Enter' && open) { e.preventDefault(); setOpen(false) }
        }}
        placeholder="ห้อง / ชั้น / จุดติดตั้ง…"
        className={`${FIELD} pl-9 pr-9`}
      />
      <button
        type="button" tabIndex={-1} onClick={() => { setOpen((v) => !v); inputRef.current?.focus() }}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600"
      >
        <ChevronDown size={15} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1.5 w-full max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 py-1">
          {loading && (
            <div className="px-3 py-2.5 text-[13px] text-slate-400 flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> กำลังโหลดที่ตั้งที่มีอยู่…</div>
          )}

          {isNew && (
            <button
              type="button" onClick={() => pick(typed)}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-brand-50 border-b border-slate-100"
            >
              <Plus size={14} className="text-brand-600 shrink-0" />
              <span className="text-[13.5px] text-slate-700 min-w-0">
                เพิ่มที่ตั้งใหม่ <b className="text-brand-700">“{typed}”</b>
              </span>
            </button>
          )}

          {!loading && ordered.map((k) => {
            const active = k.name.toLowerCase() === typed.toLowerCase()
            return (
              <button
                key={k.name} type="button" onClick={() => pick(k.name)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50"
              >
                <span className="flex items-center gap-2 min-w-0">
                  {active ? <Check size={13} className="text-brand-600 shrink-0" /> : <MapPin size={13} className="text-slate-300 shrink-0" />}
                  <span className={`text-[13.5px] truncate ${active ? 'text-brand-700 font-medium' : 'text-slate-700'}`}>{k.name}</span>
                </span>
                <span className="text-[11.5px] text-slate-400 shrink-0 tabular-nums">{k.count} ชิ้น</span>
              </button>
            )
          })}

          {!loading && !ordered.length && !isNew && (
            <div className="px-3 py-2.5 text-[13px] text-slate-400">ยังไม่มีที่ตั้งในระบบ — พิมพ์เพื่อเพิ่มใหม่</div>
          )}
        </div>
      )}
    </div>
  )
}
